import type { ApplicationEngineFilters } from 'types/applicationEngine'
import type { BenefitsSortField } from '@surecompanies/contracts/benefits'
import type { SortOrder } from '@surecompanies/contracts/pagination'
import type { BenefitsQueryDto } from '../dtos/Benefit.dto'
import type {
  ApplicationStatus,
  CarrierApplicationEscalationType,
  CarrierPlanChangeIndicator,
} from 'types/benefit'
import type { CarrierResponseStatus } from 'types/carrierResponse'
import {
  CARRIER_RESPONSE_STATUS,
  ESCALATION_CARRIER_RESPONSES,
} from 'types/carrierResponse'
import isEmpty from 'lodash/isEmpty'
import { toUrlParamArray } from 'helpers/url'
import {
  getOepFilterStart,
  getOepFilterEnd,
  DateTimeFormats,
} from 'helpers/datetime'
import {
  EnrollmentComputedStatus,
  EnrollmentType,
} from '@surecompanies/contracts'

/** Maps sidebar/URL enrollment type labels to API query values. */
const ENROLLMENT_TYPE_FILTER_TO_API = {
  OEP: EnrollmentType.OPEN,
  SEP: EnrollmentType.SPECIAL,
  QLE: 'QLE' as EnrollmentType,
} as const

const mapEnrollmentTypeFiltersToApi = (
  value: string | string[] | undefined
): EnrollmentType[] | undefined => {
  if (!value) return undefined

  const types = Array.isArray(value) ? value : [value]
  if (!types.length) return undefined

  return types.map((type) => ENROLLMENT_TYPE_FILTER_TO_API[type] ?? type)
}

/**
 * Injects the default date range when none is set in URL filters.
 * Keeps the list query and "select all" bulk-query scoped to the same window.
 */
export function applyDefaultDateRange(
  filters: ApplicationEngineFilters
): ApplicationEngineFilters {
  if (filters.benefitEffectiveStartDate && filters.benefitEffectiveEndDate) {
    return filters
  }
  return {
    ...filters,
    benefitEffectiveStartDate: getOepFilterStart().format(DateTimeFormats.a),
    benefitEffectiveEndDate: getOepFilterEnd().format(DateTimeFormats.a),
  }
}

/** Converts URL filter params to a BenefitsQueryDto for API calls. */
export function mapBenefitFiltersToQuery(
  filters: ApplicationEngineFilters
): Partial<BenefitsQueryDto> {
  return {
    search: filters.search || undefined,
    employeeName: filters.employeeName || undefined,
    // Comma-separated string params (backend expects a single string)
    companyName: Array.isArray(filters.companyName)
      ? filters.companyName.join(',')
      : filters.companyName,
    // Array params (backend expects key[]=val1&key[]=val2)
    clientPublicKey: toUrlParamArray<string>(filters.clientPublicKey),
    state: Array.isArray(filters.state)
      ? filters.state.join(',')
      : filters.state,
    assignee: Array.isArray(filters.assignee)
      ? filters.assignee.join(',')
      : filters.assignee,
    // Array params (backend expects key[]=val1&key[]=val2)
    carrierName: toUrlParamArray<string>(filters.carrierName),
    carrierApplicationStatus: toUrlParamArray<ApplicationStatus>(
      filters.carrierApplicationStatus
    ),
    cartStatus: toUrlParamArray<string>(filters.cartStatus),
    carrierPlanChange: toUrlParamArray<CarrierPlanChangeIndicator>(
      filters.carrierPlanChange
    ),
    // Date range
    benefitEffectiveStartDate: filters.benefitEffectiveStartDate,
    benefitEffectiveEndDate: filters.benefitEffectiveEndDate,
    enrollmentStart: filters.enrollmentStart,
    enrollmentEnd: filters.enrollmentEnd,
    enrollmentType: mapEnrollmentTypeFiltersToApi(filters.enrollmentType),
    enrollmentStatus: toUrlParamArray<EnrollmentComputedStatus>(
      filters.enrollmentStatus
    ),
    // Other filters
    commissionPartner: filters.commissionPartner,
    isUnassignedOnly: filters.isUnassignedOnly === 'true' ? true : undefined,
    // API isBundleSplit (split bundle) → UI: "split plan" filter
    isBundleSplit: filters.isBundleSplit === 'true' ? true : undefined,
    isTestUser:
      filters.isTestUser === 'true'
        ? true
        : filters.isTestUser === 'false'
          ? false
          : undefined,
    sort: filters.sort as BenefitsSortField | undefined,
    order: filters.order?.toUpperCase() as SortOrder | undefined,
    carrierApplicationEscalationType:
      toUrlParamArray<CarrierApplicationEscalationType>(
        filters.carrierApplicationEscalationType
      ),
    carrierApplicationCarrierResponse: toUrlParamArray<CarrierResponseStatus>(
      filters.carrierApplicationCarrierResponse
    ),
  }
}

/**
 * True when the App Engine sidebar's "Escalation" section (escalation types +
 * the escalation carrier responses) has an active filter — used to highlight
 * the matching tab and count the sidebar's active filters.
 */
export function isEscalationSectionActive(
  filters: ApplicationEngineFilters
): boolean {
  const carrierResponse =
    toUrlParamArray<CarrierResponseStatus>(
      filters.carrierApplicationCarrierResponse
    ) ?? []
  return (
    !isEmpty(filters.carrierApplicationEscalationType) ||
    carrierResponse.some((value) =>
      ESCALATION_CARRIER_RESPONSES.includes(value)
    )
  )
}

/**
 * True when the App Engine sidebar's "Carrier Confirmed" section (the green
 * "success" carrier response) has an active filter.
 */
export function isCarrierConfirmedSectionActive(
  filters: ApplicationEngineFilters
): boolean {
  const carrierResponse =
    toUrlParamArray<CarrierResponseStatus>(
      filters.carrierApplicationCarrierResponse
    ) ?? []
  return carrierResponse.includes(CARRIER_RESPONSE_STATUS.SUCCESS)
}
